'use client';

/**
 * Per-room unread tracking for the lobby's "My Boards" red-dot.
 *
 * Mirrors the single-room `useChatUnread` pattern but keys storage by
 * `(address, room)` so each board the viewer holds gets its own
 * "last seen message id" without disturbing the global chat-sheet
 * badge (which is keyed on `chat-last-seen-id:{address}` — same
 * prefix, no `:room` suffix).
 *
 * Data flow:
 *   1. `markChatRoomSeen(latestId, address, room)` is called from the
 *      lobby whenever the viewer "visits" a board (lands on its
 *      canvas + chat dock). It writes the id into localStorage under
 *      `chat-last-seen-id:{address}:{room}` and dispatches a synthetic
 *      `storage` event so the same tab also picks up the change.
 *   2. `useBoardsChatUnread(rooms[])` polls
 *      `GET /api/chat/latest?rooms=…` every 30s for the latest message
 *      id per room, then compares against the stored `lastSeenId` per
 *      room to decide whether each room is "unread".
 *
 * Rooms with no messages are simply absent from the API response —
 * the consumer treats a missing key as `latestId = 0`, which is never
 * greater than any non-zero `lastSeenId` so the dot stays off. First-
 * time visitors with `lastSeenId === 0` see the dot iff the room has
 * at least one message; this matches the existing `useChatUnread`
 * convention where "you've never marked seen" already implies
 * "everything counts as new" once the API knows about a message.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useWallet } from '@/hooks/use-wallet';
import { openDeferredEventSource } from '@/lib/open-deferred-event-source';

const KEY_PREFIX = 'chat-last-seen-id';

function storageKey(address: string, room: string): string {
  return `${KEY_PREFIX}:${address.toLowerCase()}:${room}`;
}

function readLastSeen(address: string, room: string): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(storageKey(address, room));
  if (!raw) return 0;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Mark a room seen. No-ops when:
 *   - we're on the server,
 *   - `latestId` is non-positive (nothing meaningful to record),
 *   - the stored value is already at least as fresh.
 *
 * Always emits a `storage` event on writes so subscribers in the same
 * tab pick up the change (the native `storage` event only fires
 * cross-tab; without this the lobby's My Boards dot would stay lit
 * until a remount or the next 30s poll).
 */
export function markChatRoomSeen(
  latestId: number,
  address: string,
  room: string,
): void {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(latestId) || latestId <= 0) return;
  const key = storageKey(address, room);
  const current = parseInt(window.localStorage.getItem(key) || '0', 10);
  if (latestId <= current) return;
  window.localStorage.setItem(key, String(latestId));
  // Same dispatch trick `useChatUnread.markChatSeen` uses — the native
  // `storage` event doesn't fire in the originating tab, so we synth
  // one to nudge `useSyncExternalStore` consumers in this tab.
  window.dispatchEvent(new StorageEvent('storage', { key }));
}

interface LatestRoomMeta {
  /** Latest non-deleted message id in the room. */
  id: number;
  /** Lowercased author address of that message. */
  address: string;
}

interface LatestResponse {
  rooms: Record<string, LatestRoomMeta>;
}

interface UseBoardsChatUnreadResult {
  /**
   * Map of `room → latest message id` from the server. Rooms with no
   * messages are absent (treat as `0`). Kept as a flat `id` map so
   * existing call sites (`markRoomsSeen`, ad-hoc `markChatRoomSeen`
   * callers) don't need to know about the richer `{ id, address }`
   * payload — the address is only consumed internally by `hasUnread`
   * to suppress the dot for the viewer's own messages.
   */
  latestIds: Record<string, number>;
  /**
   * True iff the room has a newer message than the locally stored
   * last-seen id AND that newest message wasn't sent by the viewer.
   * Your own messages never light the dot — this matches the user
   * expectation that "notifications" are about other people's
   * activity, not your own writes.
   */
  hasUnread: (room: string) => boolean;
  /** Loading state for the initial fetch — UI consumers may skip the dot until this clears. */
  isLoading: boolean;
}

/**
 * Subscribe to per-room latest ids + per-room last-seen storage.
 *
 * Pass the list of rooms the viewer cares about (typically the boards
 * they hold tiles on, plus any extras the consumer wants to track).
 * The hook canonicalises duplicates and stable-stringifies the list
 * for the query key so two callers with the same rooms in different
 * order share the cache.
 */
export function useBoardsChatUnread(
  rooms: string[],
): UseBoardsChatUnreadResult {
  const { address, getAccessToken } = useWallet();
  const queryClient = useQueryClient();

  // Canonicalise via a content-derived string first so callers passing
  // fresh array literals (e.g. `[activeBoardId]` from the LobbyClient
  // mark-seen wiring) don't bust the memo every render. Identity of
  // the input array doesn't matter — only the set of rooms does.
  const roomsKey = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of rooms) {
      if (!r) continue;
      const c = r.toLowerCase();
      if (seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
    out.sort();
    return out.join(',');
  }, [rooms]);

  const canonicalRooms = useMemo(
    () => (roomsKey ? roomsKey.split(',') : []),
    [roomsKey],
  );

  const queryKey = useMemo(
    () => ['chat-latest', roomsKey] as const,
    [roomsKey],
  );

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<LatestResponse> => {
      if (canonicalRooms.length === 0) return { rooms: {} };
      const qs = new URLSearchParams({ rooms: canonicalRooms.join(',') });
      const res = await fetch(`/api/chat/latest?${qs.toString()}`);
      if (!res.ok) return { rooms: {} };
      const json = (await res.json()) as Partial<LatestResponse>;
      // Defensive: the server now returns `{ id, address }` per room,
      // but we still tolerate a stale build/cache that sends a bare
      // numeric id. The fallback keeps the dot logic working (it just
      // can't suppress the viewer's own messages until the server
      // catches up).
      const rooms: Record<string, LatestRoomMeta> = {};
      const raw = (json.rooms ?? {}) as Record<string, LatestRoomMeta | number>;
      for (const [room, value] of Object.entries(raw)) {
        if (typeof value === 'number') {
          if (Number.isFinite(value) && value > 0) {
            rooms[room] = { id: value, address: '' };
          }
        } else if (value && typeof value === 'object' && Number.isFinite(value.id) && value.id > 0) {
          rooms[room] = { id: value.id, address: (value.address ?? '').toLowerCase() };
        }
      }
      return { rooms };
    },
    enabled: canonicalRooms.length > 0,
    // Lobby My Boards refreshes at the same cadence as the global
    // chat-sheet badge (`useChatUnread`) — 30s is well below
    // typical board-switch dwell time and matches the implicit
    // poll the user already lives with on the bell. Polling stays
    // in place as the resilience floor under the SSE subscription
    // below (covers connection drops, cold starts, and the brief
    // window before the EventSource lands its first event).
    staleTime: 30_000,
    refetchInterval: canonicalRooms.length > 0 ? 30_000 : false,
    refetchOnWindowFocus: true,
  });

  // Real-time companion to the 30s poll above — opens an SSE connection
  // to `/api/chat/latest/stream` for the canonical rooms set and merges
  // each `latest` event into the React Query cache directly.  This is
  // the only path that lets red dots appear without ~30s of perceived
  // latency when a peer posts in a room the viewer isn't actively
  // watching (chat panel closed / sitting on a different board).
  //
  // The bus only fires `message` events, so deletes/edits still rely on
  // the next poll to settle — see `route.ts` for the rationale.
  //
  // Stale-id guard: we never overwrite a higher cached id with an older
  // event. Fan-out can deliver out-of-order between the SSE and a
  // concurrent poll completing (e.g. SSE delivers id=42, poll lands with
  // id=43, a slow SSE retransmission of id=42 would otherwise stomp 43).
  useEffect(() => {
    if (canonicalRooms.length === 0 || !address) return undefined;
    let close: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (cancelled || !token) return;
      const url = `/api/chat/latest/stream?token=${encodeURIComponent(token)}&rooms=${encodeURIComponent(canonicalRooms.join(','))}`;
      close = openDeferredEventSource(url, (es) => {
        es.addEventListener('latest', (e) => {
          try {
            const payload = JSON.parse((e as MessageEvent).data) as {
              room?: string;
              id?: number;
              address?: string | null;
            };
            const room = (payload.room ?? '').toLowerCase();
            const id = Number(payload.id);
            if (!room || !Number.isFinite(id) || id <= 0) return;
            const addr = (payload.address ?? '').toLowerCase();
            queryClient.setQueryData<LatestResponse>(queryKey, (old) => {
              const prev = old?.rooms?.[room];
              if (prev && prev.id >= id) return old;
              return {
                rooms: {
                  ...(old?.rooms ?? {}),
                  [room]: { id, address: addr },
                },
              };
            });
          } catch {
            // Ignore malformed payloads — the next poll will reconcile.
          }
        });
        // No `onerror` retry handler here: `openDeferredEventSource`
        // tears the EventSource down on cleanup, and the effect re-runs
        // when the rooms list / address / token hook change. Browsers
        // also auto-reconnect EventSources on transient network blips
        // by default, so leaving this empty matches the pattern in
        // `useChatStream` / `useNotificationStream`.
      });
    })();
    return () => {
      cancelled = true;
      close?.();
    };
  }, [canonicalRooms, address, queryKey, queryClient, getAccessToken]);

  // Subscribe to localStorage for last-seen id changes. We snapshot
  // the entire `(address, rooms)` matrix as a single stable string so
  // `useSyncExternalStore` only re-renders when one of the watched
  // rooms actually changes — picking the smallest scalar payload that
  // preserves room ordering avoids extra renders when an unrelated
  // tab writes some other key.
  const subscribe = useCallback((cb: () => void) => {
    if (typeof window === 'undefined') return () => {};
    const onStorage = () => cb();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const getSnapshot = useCallback(() => {
    if (!address) return '';
    const parts: string[] = [];
    for (const r of canonicalRooms) {
      parts.push(`${r}:${readLastSeen(address, r)}`);
    }
    return parts.join('|');
  }, [address, canonicalRooms]);

  // SSR snapshot is always empty — we can't read localStorage on the
  // server, and an empty string keeps the snapshot stable across the
  // hydration boundary so React doesn't tear.
  const lastSeenSnapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => '',
  );

  const lastSeenMap = useMemo(() => {
    const m = new Map<string, number>();
    if (!lastSeenSnapshot) return m;
    for (const part of lastSeenSnapshot.split('|')) {
      const sep = part.lastIndexOf(':');
      if (sep <= 0) continue;
      const room = part.slice(0, sep);
      const id = parseInt(part.slice(sep + 1), 10);
      if (Number.isFinite(id)) m.set(room, id);
    }
    return m;
  }, [lastSeenSnapshot]);

  const latestMeta = data?.rooms ?? {};

  // Project the rich `{ id, address }` map down to the flat
  // `room → id` shape that external consumers expect. Memoised on the
  // raw meta reference so callers using it as a dep array (e.g. the
  // `markRoomsSeen` effect in `BoardDirectory` / `MobileChatBoardList`)
  // don't loop on every render.
  const latestIds = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [room, meta] of Object.entries(latestMeta)) {
      out[room] = meta.id;
    }
    return out;
  }, [latestMeta]);

  const viewerLower = address ? address.toLowerCase() : '';

  const hasUnread = useCallback(
    (room: string) => {
      if (!address) return false;
      const canonical = room.toLowerCase();
      const meta = latestMeta[canonical];
      if (!meta || meta.id <= 0) return false;
      // Suppress the dot when the newest message in this room is the
      // viewer's own — you don't notify yourself about what you just
      // sent. We still update `lastSeen` semantics elsewhere, but the
      // dot is what end users actually perceive as a notification.
      if (meta.address && meta.address === viewerLower) return false;
      const lastSeen = lastSeenMap.get(canonical) ?? 0;
      return meta.id > lastSeen;
    },
    [address, latestMeta, lastSeenMap, viewerLower],
  );

  return { latestIds, hasUnread, isLoading };
}
